import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { sortBy } from 'lodash'
import React, { FC, useCallback, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation, useParams } from 'react-router-dom'
import { RootState } from '../../../../states/containers/store'
import { fetchTaskPermission, updateTaskPermission } from '../../../../states/containers/task/taskSlice'
import LoadingSquare from '../../../components/common/LoadingSquare'
import Tips from '../../../components/common/Tips'
import AdminModal from '../../../components/task/AdminModal'
import { useAlert } from 'react-alert'
import { compile } from '../../../../utils/search'
import { setPath } from '../../../../utils/url'
import { LangValue } from '../../../../states/types/common'
import { URL } from '../../../constants/url'

interface Member {
  id: number
  name: string
  kana: string | null
  email: string
  code: string | null
  profile_image: string | null
  active: boolean
  team_name: string
  cost_view: boolean
  member_cost_setting: boolean
  project_cost_setting: boolean
  project_setting: boolean
  attendance_export: boolean
  project_member_setting: boolean
}

const DISPLAY_BLOCK_NUMBER = 100

const Index: FC = () => {
  const {
    login,
  } = useSelector((state: RootState) => state)

  const location = useLocation()
  const dispatch = useDispatch()
  const alert = useAlert()
  const { language } = useParams<{ language: LangValue }>()

  const [composition, setComposition] = useState<boolean>(false)
  const [searchWord, setSearchWord] = useState<string>("")
  const [memberList, setMemberList] = useState<Member[]>([])
  const [originMemberList, setOriginMemberList] = useState<Member[]>([])

  const [isDisplayModal, setIsDisplayModal] = useState<boolean>(false)

  const [checkedMemberIds, setCheckedMemberIds] = useState<number[]>([])
  const [targetMembers, setTargetMembers] = useState<Member[]>([])

  const [isLoading, setIsLoading] = useState<boolean>(true)

  const [displayBlockNum, setDisplayBlockNum] = useState<number>(1)

  const currentScroll = () => Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop)
  const bottomHeight = () => document.getElementsByClassName("main")[0].clientHeight
  const windowHeight = () => document.documentElement.clientHeight

  const onScroll = useCallback(() => {
    const adjustment = window.innerWidth >= 1280 ? 0.90 : 0.85
    if (
      (bottomHeight() * adjustment) < (currentScroll() + windowHeight())
      && memberList.length >= displayBlockNum * DISPLAY_BLOCK_NUMBER
    ) {
      setDisplayBlockNum((self) => self + 1)
    }
  }, [displayBlockNum, memberList])

  const clickCheckbox = (memberId: number) => {
    if (checkedMemberIds.includes(memberId)) {
      setCheckedMemberIds(checkedMemberIds.filter((v) => v !== memberId))
    } else {
      setCheckedMemberIds([...checkedMemberIds, memberId])
    }
  }

  const keyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !composition) {
      event.currentTarget.blur()
    }
  }

  const reset = () => {
    setSearchWord("")
    setMemberList(originMemberList)
  }

  const search = (originalList: Member[] | undefined) => {
    if (!searchWord) {
      reset()
      setDisplayBlockNum(1)
      return
    }
    const word = searchWord.split(/\s+/)
    const list = originalList?.filter((v) => {
      if (v === null) return false
      return (compile(word).test(v.name + v.kana + v.code + v.email + v.team_name))
    }) as Member[]
    setMemberList(sortBy(list, 'id'))
    const memberIds = list.map((v) => v.id)
    setCheckedMemberIds((cur) => cur.filter((v) => memberIds.includes(v)))
    setDisplayBlockNum(1)
  }

  const modAdmin = (
    memberIds: number[],
    projectSetting: boolean,
    costView: boolean,
    memberCostSetting: boolean,
    projectCostSetting: boolean,
    attendanceExport: boolean,
    projectMemberSetting: boolean,
  ) => {
    setIsDisplayModal(false)
    const param = {
      member_ids: memberIds,
      project_setting: projectSetting,
      cost_view: costView,
      member_cost_setting: memberCostSetting,
      project_cost_setting: projectCostSetting,
      attendance_export: attendanceExport,
      project_member_setting: projectMemberSetting,
    }
    dispatch(
      updateTaskPermission(
        param,
        () => {
          alert.success("権限を編集しました")
          setMemberList((cur) => cur.map((v) => (memberIds.includes(v.id) ? { ...v, ...param } : v)))
          setOriginMemberList((cur) => cur.map((v) => (memberIds.includes(v.id) ? { ...v, ...param } : v)))
        },
      ),
    )
  }

  const closeModal = () => {
    setIsDisplayModal(false)
  }

  const openModal = (targetMemberIds: number[]) => {
    setTargetMembers(originMemberList.filter((v) => targetMemberIds.includes(v.id)))
    setIsDisplayModal(true)
  }

  const buildMatrix = (status: string) => {
    let ele = <></>
    if (status === "project_setting") {
      ele = (
        <>
          <FontAwesomeIcon icon={['fas', 'cogs']} />
          プロジェクト・カテゴリー設定
        </>
      )
    } else if (status === "cost_view") {
      ele = (
        <>
          <FontAwesomeIcon icon={['fas', 'eye']} />
          コスト表示
        </>
      )
    } else if (status === "member_cost_setting") {
      ele = (
        <>
          <FontAwesomeIcon icon={['fas', 'coins']} />
          ユーザーコスト設定
        </>
      )
    } else if (status === "project_cost_setting") {
      ele = (
        <>
          <FontAwesomeIcon icon={['fas', 'coins']} />
          プロジェクトコスト設定
        </>
      )
    } else if (status === "attendance_export") {
      ele = (
        <>
          <FontAwesomeIcon icon={['fas', 'file-download']} />
          勤怠データエクスポート
        </>
      )
    } else if (status === "project_member_setting") {
      ele = (
        <>
          <FontAwesomeIcon icon={['fas', 'users']} />
          プロジェクト参加ユーザー設定
        </>
      )
    }
    return (
      <tr>
        <td>
          <div className="label_wrapper">
            <div className={`label ${status}`}>
              {ele}
            </div>
          </div>
        </td>
        <td>
          {
            status === "project_setting" ? (
              <FontAwesomeIcon icon={['fas', 'check']} className="icon green" />
            ) : (
              <FontAwesomeIcon icon={['fas', 'times']} className="icon red" />
            )
          }
        </td>
        <td>
          {
            status === "cost_view" ? (
              <FontAwesomeIcon icon={['fas', 'check']} className="icon green" />
            ) : (
              <FontAwesomeIcon icon={['fas', 'times']} className="icon red" />
            )
          }
        </td>
        <td>
          {
            status === "member_cost_setting" ? (
              <FontAwesomeIcon icon={['fas', 'check']} className="icon green" />
            ) : (
              <FontAwesomeIcon icon={['fas', 'times']} className="icon red" />
            )
          }
        </td>
        <td>
          {
            status === "project_cost_setting" ? (
              <FontAwesomeIcon icon={['fas', 'check']} className="icon green" />
            ) : (
              <FontAwesomeIcon icon={['fas', 'times']} className="icon red" />
            )
          }
        </td>
        {
          login.function_enabled_state?.task_management_approval && (
            <td>
              {
                status === "attendance_export" ? (
                  <FontAwesomeIcon icon={['fas', 'check']} className="icon green" />
                ) : (
                  <FontAwesomeIcon icon={['fas', 'times']} className="icon red" />
                )
              }
            </td>
          )
        }
        {
          login.feature_config?.enable_project_member_setting && (
            <td>
              {
                (status === "project_setting" || status === "project_member_setting") ? (
                  <FontAwesomeIcon icon={['fas', 'check']} className="icon green" />
                ) : (
                  <FontAwesomeIcon icon={['fas', 'times']} className="icon red" />
                )
              }
            </td>
          )
        }
      </tr>
    )
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location])

  useEffect(() => {
    setIsLoading(true)
    dispatch(fetchTaskPermission(
      (result) => {
        setOriginMemberList(sortBy(result?.members, 'id'))
        setMemberList(sortBy(result?.members, 'id'))
        setIsLoading(false)
      },
    ))
  }, [])

  useEffect(() => {
    document.addEventListener("scroll", onScroll)
    return () => {
      document.removeEventListener("scroll", onScroll)
    }
  }, [onScroll])

  return (
    <section className="task_page">
      <Helmet>
        <title>
          {
            location.pathname === `/${language}${URL.SETTING_TASK_PERMISSION.path}` ? (
              `Qasee - 設定 - ${login.role_name === "管理者" ? "管理者" : "共通"}設定 - 工数管理詳細権限設定`
            ) : (
              `Qasee - 工数管理 - 工数管理詳細権限設定`
            )
          }
        </title>
      </Helmet>
      <section className="breadcrumb">
        {
          location.pathname === `/${language}${URL.SETTING_TASK_PERMISSION.path}` ? (
            <>
              設定
              &nbsp;&gt;&nbsp;
              <Link
                to={setPath(URL.SETTING_COMMON.path, language)}
              >
                {`${login.role_name === "管理者" ? "管理者" : "共通"}設定`}
              </Link>
            </>
          ) : (
            <>
              工数管理
            </>
          )
        }
        &nbsp;&gt;&nbsp;
        工数管理詳細権限設定
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          工数管理詳細権限設定
        </h1>
      </section>
      <section className="page_description">
        <p>
          ユーザーに工数管理詳細権限を付与することができます。
        </p>
        <p>
          設定すると権限に応じて、表示や設定が可能になります。
        </p>
        <table className="permission_example">
          <thead>
            <tr>
              <th>
                権限 / できること
              </th>
              <th>
                <div
                  style={{
                    display: "flex",
                    flexFlow: "column",
                    gap: "4px",
                  }}
                >
                  <div>
                    <div>
                      プロジェクトの
                    </div>
                    <div>
                      作成・編集
                    </div>
                  </div>
                  <div>
                    <div>
                      カテゴリーの
                    </div>
                    <div>
                      作成・編集
                    </div>
                  </div>
                </div>
              </th>
              <th>
                <div>
                  <div>
                    プロジェクトの
                  </div>
                  <div>
                    コスト表示
                  </div>
                </div>
              </th>
              <th>
                <div>
                  <div>
                    ユーザーの
                  </div>
                  <div>
                    コスト設定
                  </div>
                </div>
              </th>
              <th>
                <div>
                  <div>
                    プロジェクトの
                  </div>
                  <div>
                    コスト設定
                  </div>
                </div>
              </th>
              {
                login.function_enabled_state?.task_management_approval && (
                  <>
                    <th>
                      <div>
                        <div>
                          勤怠の
                        </div>
                        <div>
                          データエクスポート
                        </div>
                      </div>
                    </th>
                  </>
                )
              }
              {
                login.feature_config?.enable_project_member_setting && (
                  <>
                    <th>
                      <div>
                        <div>
                          プロジェクトの
                        </div>
                        <div>
                          参加ユーザー設定
                        </div>
                      </div>
                    </th>
                  </>
                )
              }
            </tr>
          </thead>
          <tbody>
            {buildMatrix("project_setting")}
            {buildMatrix("cost_view")}
            {buildMatrix("member_cost_setting")}
            {buildMatrix("project_cost_setting")}
            {login.function_enabled_state?.task_management_approval && buildMatrix("attendance_export")}
            {login.feature_config?.enable_project_member_setting && buildMatrix("project_member_setting")}
          </tbody>
        </table>
      </section>
      {
        isLoading ? (
          <LoadingSquare>
            <>
              設定を読み込んでいます…
            </>
          </LoadingSquare>
        ) : (
          <>
            <section className="page_contents content-area">
              <div className="content-block">
                <div className="block-header">
                  <div className="title">
                    ユーザーの個別設定
                  </div>
                  <div className="search_area">
                    <input
                      type="text"
                      placeholder="名前・メールアドレス・チーム名でユーザー検索"
                      onChange={(event) => setSearchWord(event.target.value)}
                      onBlur={() => search(originMemberList)}
                      onKeyDown={(event) => keyDown(event)}
                      onCompositionStart={() => setComposition(true)}
                      onCompositionEnd={() => setComposition(false)}
                      value={searchWord}
                      style={{ width: "300px", fontSize: "12px" }}
                    />
                    {
                      searchWord && (
                        <button type="button" className="reset" onClick={reset}>
                          <FontAwesomeIcon icon={['far', 'times-circle']} />
                        </button>
                      )
                    }
                  </div>
                </div>
                <div className="block-detail">
                  <div
                    style={{
                      border: `solid 1px ${checkedMemberIds.length ? "#228b22" : "#999999"}`,
                      borderRadius: "4px",
                      backgroundColor: checkedMemberIds.length ? "#f3fff9" : "#EEEEEE",
                      padding: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "right",
                      gap: "12px",
                      marginBottom: "16px",
                      position: "sticky",
                      top: "60px",
                      zIndex: "1",
                    }}
                  >
                    {
                      checkedMemberIds.length ? (
                        <>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "baseline",
                              gap: "2px",
                            }}
                          >
                            <div
                              style={{
                                color: "#228b22",
                                fontSize: "20px",
                                fontWeight: "bold",
                              }}
                            >
                              {checkedMemberIds.length}
                            </div>
                            <div>
                              名のユーザーが選択されています
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => openModal(checkedMemberIds)}
                            style={{
                              border: "1px solid #228b22",
                              borderRadius: "4px",
                              backgroundColor: "#228b22",
                              color: "#FFFFFF",
                              display: "flex",
                              gap: "4px",
                            }}
                          >
                            <FontAwesomeIcon
                              icon={["fas", "check"]}
                              fixedWidth
                            />
                            まとめて変更する
                          </button>
                        </>
                      ) : (
                        <>
                          <div
                            style={{
                              marginTop: "8px",
                            }}
                          >
                            ユーザーが選択されていません
                          </div>
                          <button
                            type="button"
                            disabled
                            style={{
                              border: "1px solid #DDDDDD",
                              borderRadius: "4px",
                              backgroundColor: "#DDDDDD",
                              color: "#999999",
                              display: "flex",
                              gap: "4px",
                              cursor: "not-allowed",
                            }}
                          >
                            <FontAwesomeIcon
                              icon={["fas", "check"]}
                              fixedWidth
                            />
                            まとめて変更する
                          </button>
                        </>
                      )
                    }
                  </div>
                  {
                    memberList.length ? (
                      <>
                        <table className="flex-table">
                          <thead>
                            <tr>
                              <th style={{ width: "30px" }}>
                                <div
                                  style={{
                                    fontSize: "16px",
                                    color: checkedMemberIds.length === memberList.length ? "#008800" : "#666666",
                                    position: "relative",
                                    cursor: "pointer",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: "8px",
                                      color: "#999999",
                                      whiteSpace: "nowrap",
                                      position: "absolute",
                                      top: "-10px",
                                      left: "50%",
                                      transform: "translateX(-50%)",
                                    }}
                                  >
                                    全選択
                                  </div>
                                  {
                                    checkedMemberIds.length === memberList.length ? (
                                      <FontAwesomeIcon icon={['far', 'check-square']} onClick={() => setCheckedMemberIds([])} />
                                    ) : (
                                      <FontAwesomeIcon icon={['far', 'square']} onClick={() => setCheckedMemberIds(memberList.map((v) => v.id))} />
                                    )
                                  }
                                </div>
                              </th>
                              <th style={{ width: "50px", justifyContent: "center" }}>
                                操作
                              </th>
                              <th style={{ width: "20%", justifyContent: "center" }}>
                                名前
                              </th>
                              <th style={{ width: "20%", justifyContent: "center" }}>
                                メールアドレス
                              </th>
                              <th style={{ width: "20%", justifyContent: "center" }}>
                                チーム
                              </th>
                              <th style={{ width: "calc(40% - 80px)", justifyContent: "center" }}>
                                権限
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {
                              memberList.slice(0, displayBlockNum * DISPLAY_BLOCK_NUMBER).map((member) => (
                                <tr
                                  key={`${member.id}_${member.email}`}
                                  role="none"
                                  onClick={() => clickCheckbox(member.id)}
                                  style={{
                                    backgroundColor: checkedMemberIds.includes(member.id) ? "#f3fff9" : "",
                                    cursor: "pointer",
                                  }}
                                >
                                  <td style={{ width: "30px" }}>
                                    <div
                                      style={{
                                        fontSize: "16px",
                                        color: checkedMemberIds.includes(member.id) ? "#008800" : "#666666",
                                      }}
                                    >
                                      {
                                        checkedMemberIds.includes(member.id) ? (
                                          <FontAwesomeIcon icon={['far', 'check-square']} />
                                        ) : (
                                          <FontAwesomeIcon icon={['far', 'square']} />
                                        )
                                      }
                                    </div>
                                  </td>
                                  <td style={{ width: "50px", justifyContent: "center" }}>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <Tips
                                        description="編集"
                                        className=""
                                        icon={false}
                                      >
                                        <button
                                          className="edit"
                                          type="button"
                                          onClick={(e) => {
                                            openModal([member.id])
                                            e.stopPropagation()
                                          }}
                                          style={{
                                            color: "#666666",
                                            fontSize: "20px",
                                          }}
                                        >
                                          <FontAwesomeIcon icon={['fas', 'tools']} />
                                        </button>
                                      </Tips>
                                    </div>
                                  </td>
                                  <td style={{ width: "20%", justifyContent: "center", gap: "4px" }}>
                                    <img
                                      alt={member.name}
                                      src={member.profile_image ?? `/static/images/profile/${(Number(member.id) || 0) % 12}.jpeg`}
                                      style={{ width: "16px", height: "16px", borderRadius: "50%" }}
                                    />
                                    <ruby>
                                      {member.name}
                                      <rt>{member.kana}</rt>
                                    </ruby>
                                    {member.code ? ` (${member.code})` : ""}
                                    {
                                      member.active || (
                                        <div>
                                          <Tips
                                            description="休眠中💤"
                                            className=""
                                            icon={false}
                                            style={{
                                              position: "relative",
                                            }}
                                          >
                                            <>
                                              💤
                                            </>
                                          </Tips>
                                        </div>
                                      )
                                    }
                                  </td>
                                  <td style={{ width: "20%", justifyContent: "center" }}>
                                    <div
                                      style={{
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        width: "100%",
                                        minWidth: 0,
                                        textAlign: "center",
                                      }}
                                    >
                                      {member.email}
                                    </div>
                                  </td>
                                  <td style={{ width: "20%", justifyContent: "center" }}>
                                    <div
                                      style={{
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        width: "100%",
                                        minWidth: 0,
                                        textAlign: "center",
                                      }}
                                    >
                                      {member.team_name || "所属チームなし"}
                                    </div>
                                  </td>
                                  <td style={{ width: "calc(40% - 80px)" }}>
                                    <div
                                      style={{
                                        display: "flex",
                                        flexFlow: "column",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        gap: "2px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          flexWrap: "wrap",
                                          gap: "8px",
                                        }}
                                      >
                                        {
                                          member.project_setting && (
                                            <Tips
                                              description="プロジェクト・カテゴリーを設定・編集することができます"
                                              className=""
                                              icon={false}
                                              style={{
                                                height: "auto",
                                              }}
                                            >
                                              <div className="permission_label project_setting">
                                                <FontAwesomeIcon icon={['fas', 'cogs']} />
                                                プロジェクト・カテゴリー設定
                                              </div>
                                            </Tips>
                                          )
                                        }
                                        {
                                          member.cost_view && (
                                            <Tips
                                              description="プロジェクトにかかったコストを表示することができます"
                                              className=""
                                              icon={false}
                                              style={{
                                                height: "auto",
                                              }}
                                            >
                                              <div className="permission_label cost_view">
                                                <FontAwesomeIcon icon={['fas', 'eye']} />
                                                コスト表示
                                              </div>
                                            </Tips>
                                          )
                                        }
                                        {
                                          member.member_cost_setting && (
                                            <Tips
                                              description="ユーザーのコストを設定することができます"
                                              className=""
                                              icon={false}
                                              style={{
                                                height: "auto",
                                              }}
                                            >
                                              <div className="permission_label member_cost_setting">
                                                <FontAwesomeIcon icon={['fas', 'coins']} />
                                                ユーザーコスト設定
                                              </div>
                                            </Tips>
                                          )
                                        }
                                        {
                                          member.project_cost_setting && (
                                            <Tips
                                              description="プロジェクトのコストを設定することができます"
                                              className=""
                                              icon={false}
                                              style={{
                                                height: "auto",
                                              }}
                                            >
                                              <div className="permission_label project_cost_setting">
                                                <FontAwesomeIcon icon={['fas', 'coins']} />
                                                プロジェクトコスト設定
                                              </div>
                                            </Tips>
                                          )
                                        }
                                        {
                                          login.function_enabled_state?.task_management_approval && member.attendance_export && (
                                            <Tips
                                              description="勤怠データをCSV出力することができます"
                                              className=""
                                              icon={false}
                                              style={{
                                                height: "auto",
                                              }}
                                            >
                                              <div className="permission_label attendance_export">
                                                <FontAwesomeIcon icon={['fas', 'file-download']} />
                                                勤怠データエクスポート
                                              </div>
                                            </Tips>
                                          )
                                        }
                                        {
                                          member.project_member_setting && (
                                            <Tips
                                              description="プロジェクトの参加ユーザーを設定することができます"
                                              className=""
                                              icon={false}
                                              style={{
                                                height: "auto",
                                              }}
                                            >
                                              <div className="permission_label project_member_setting">
                                                <FontAwesomeIcon icon={['fas', 'users']} />
                                                プロジェクト参加ユーザー設定
                                              </div>
                                            </Tips>
                                          )
                                        }
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            }
                          </tbody>
                        </table>
                      </>
                    ) : (
                      <>
                        <div
                          style={{
                            textAlign: "center",
                            padding: "8px",
                            fontSize: "20px",
                            fontWeight: "bold",
                            color: "#999999",
                          }}
                        >
                          対象がありません
                        </div>
                      </>
                    )
                  }
                </div>
              </div>
            </section>
          </>
        )
      }
      <AdminModal
        targetMemberList={targetMembers}
        closeCallback={closeModal}
        modCallback={
          (memberIds, projectSetting, costView, memberCostSetting, projectCostSetting, attendanceExport, projectMemberSetting) => {
            modAdmin(memberIds, projectSetting, costView, memberCostSetting, projectCostSetting, attendanceExport, projectMemberSetting)
          }
        }
        isDisplay={isDisplayModal}
      />
    </section>
  )
}

export default Index
